import axios, { AxiosError } from "axios";
import { useState } from "react";
axios.defaults.withCredentials = true;

type APIParams = {
    url: string;
    body?: any;
    method: 'post' | 'put' | 'get' | 'delete';
    language?: any
}

const usePost = () => {
    const [loading, setLoading] = useState(false);
    const PostMethod = async ({ url, body, method, language = 'en' }: APIParams) => {
        setLoading(true);

        // Use client-side proxy URL to avoid CORS (this hook only runs in browser)
        // Auto-derive proxy path from NEXT_PUBLIC_BASE_URL if client URL not set
        let baseURL = process.env.NEXT_PUBLIC_CLIENT_BASE_URL;
        if (!baseURL && process.env.NEXT_PUBLIC_BASE_URL) {
            try {
                const parsed = new URL(process.env.NEXT_PUBLIC_BASE_URL);
                baseURL = '/api' + parsed.pathname;
            } catch {
                baseURL = process.env.NEXT_PUBLIC_BASE_URL;
            }
        }
        baseURL = baseURL || process.env.NEXT_PUBLIC_BASE_URL;
        const fullURL = `${baseURL}${url}`;

        // Identify the source of the request (web-desktop / web-mobile) so the
        // backend can attribute orders to the correct platform.
        const xClient = typeof window !== 'undefined'
            ? (window.matchMedia('(max-width: 768px)').matches ? 'web-mobile' : 'web-desktop')
            : undefined;

        try {
            const response = await axios({
                method,
                url: fullURL,
                data: body && body,
                // Cap every client-side request at 12s. The backend's
                // product/list endpoint with a ?category= filter hangs
                // indefinitely for some combos; without this, axios waits
                // forever and the caller's loading state never clears,
                // leaving skeleton placeholders on screen forever.
                timeout: 12000,
                headers: {
                    language: language,
                    ...(xClient && { 'X-Client': xClient }),
                }
            });

            return response.data;

        } catch (e) {
            const error = e as AxiosError;
            // Distinguish timeout from HTTP error: ECONNABORTED means the
            // axios timeout fired (request was aborted client-side, no
            // response body). Return a normalised failure object so callers
            // fall into their own empty-data / NoProductFound branch.
            if (error?.code === 'ECONNABORTED') {
                console.error(`[usePost] timeout after 12000ms: ${fullURL}`);
                return { success: false, message: 'Request timeout', data: null };
            }
            return error?.response?.data;
        } finally {
            setLoading(false);
        }
    };

    return { loading, PostMethod };
};

export default usePost;
